import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAccessControl } from '@plerionhq/design-system';
import type { VisibilityState } from '@tanstack/react-table';
import {
  DEFAULT_GRANTS_VIEW,
  matchGrantsView,
  shouldClampGrantsView,
  viewColumnVisibility,
  type GrantsView,
} from './grants-views';

// The view is persisted in the URL hash so a refresh restores it. Only the
// non-default view is written, to keep URLs clean. Kept out of the query string
// on purpose: the grants list query keys off search, not the hash. The hash
// matches the view's label, not its internal id — the URL is user facing, so
// #access-review reads as the tab it lands you on.
const VIEW_HASH: Record<GrantsView, string> = {
  list: '',
  audit: 'access-review',
};

function readViewFromHash(): GrantsView {
  if (typeof window === 'undefined') {
    return DEFAULT_GRANTS_VIEW;
  }
  const hash = window.location.hash.replace(/^#/, '');
  const match = (Object.keys(VIEW_HASH) as GrantsView[]).find(
    (view) => VIEW_HASH[view] !== '' && VIEW_HASH[view] === hash,
  );
  return match ?? DEFAULT_GRANTS_VIEW;
}

function writeViewToHash(view: GrantsView): void {
  if (typeof window === 'undefined') {
    return;
  }
  const url = new URL(window.location.href);
  if (url.hash.replace(/^#/, '') === VIEW_HASH[view]) {
    return;
  }
  url.hash = VIEW_HASH[view];
  window.history.replaceState(null, '', url);
}

/**
 * Column visibility for the List / Access review views.
 *
 * Lives here, above the table, because the table unmounts whenever a filter
 * change puts the list query back into `pending` — if the state sat in the
 * table it would be rebuilt from the hash on every filter change. Preset chips
 * and the sidebar both write the query string through helpers that drop the
 * hash, so by then the hash is gone and the view would silently fall back to
 * List. Re-asserting the hash after each URL change keeps a reload honest too.
 */
export function useGrantsView() {
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
    () => viewColumnVisibility(readViewFromHash()),
  );
  const { search } = useLocation();
  const { can, isLoading } = useAccessControl();
  const canReview = can({ action: 'AccessGrant:Review' });

  // The toggle does not offer Access review without AccessGrant:Review, but the
  // view also comes from the URL hash, so a shared or bookmarked
  // #access-review link would still land a non-reviewer on it.
  //
  // Derived rather than corrected in an effect: writing the clamped preset back
  // into state would fight the initializer, and it has to wait for entitlements
  // anyway, since `can` is false while they resolve and clamping then would
  // drop a reviewer back to List on every load. The effect below writes the
  // clamped view to the hash, so the URL follows what is actually shown.
  const clampToDefault = shouldClampGrantsView({
    view: matchGrantsView(columnVisibility),
    canReview,
    isLoading,
  });
  const effectiveVisibility = clampToDefault
    ? viewColumnVisibility(DEFAULT_GRANTS_VIEW)
    : columnVisibility;
  const activeView = matchGrantsView(effectiveVisibility);

  useEffect(() => {
    if (activeView) {
      writeViewToHash(activeView);
    }
  }, [activeView, search]);

  // Stable identity: the table's heading takes this through context, and a
  // fresh function each render would churn that context needlessly.
  const selectView = useCallback((next: GrantsView) => {
    setColumnVisibility(viewColumnVisibility(next));
    writeViewToHash(next);
  }, []);

  return {
    columnVisibility: effectiveVisibility,
    setColumnVisibility,
    activeView,
    selectView,
  };
}
